import React, { memo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Button, Surface } from '@americanexpress/dls-react';
import { IconClose } from '@americanexpress/dls-icons';
import {
  getEndDate,
  getIsDateRange,
  getMinShownDateString,
  getStartDate,
} from '../../selectors/dateSelectors';
import {
  geIsOutSideClickCloseEnabled,
  geIsEnableCloseButtonForDesktop,
} from '../../selectors/commonSelectors';
import {
  getEndTime,
  getHasTimeDropdown,
  getStartTime,
  getNewLabelEnabled,
} from '../../selectors/timeSelectors';
import CalendarHorizontal from '../CalendarHorizontal/CalendarHorizontal';
import { useDateSelector } from '../../context/dateContext';
import { setEndDate, setStartDate } from '../../ducks/dateDuck';
import CalendarFooter from '../CalendarFooter/CalendarFooter';
import styles from './PopupDesktop.scss';
import { setEndTime, setStartTime } from '../../ducks/timeDuck';
import { useTimeSelector } from '../../context/timeContext';
import DateTimeField from '../DateTimeField/DateTimeField';
import { convertDefaultFormatStringDateToLocaleFormat } from '../../utils/dateUtils';
import { getTimeLabels } from '../../utils/timeUtils';

const PopupDesktop = ({ onClosePopup, placeholder }) => {
  const startDate = useDateSelector(getStartDate);
  const endDate = useDateSelector(getEndDate);
  const startDateLocalFormat = convertDefaultFormatStringDateToLocaleFormat(startDate);
  const endDateLocalFormat = convertDefaultFormatStringDateToLocaleFormat(endDate);
  const isDateRange = useDateSelector(getIsDateRange);
  const minShownDate = useDateSelector(getMinShownDateString);
  const hasTimeDropdown = useTimeSelector(getHasTimeDropdown);
  const startTime = useTimeSelector(getStartTime);
  const endTime = useTimeSelector(getEndTime);
  const isOutSideClickCloseEnabled = useDateSelector(
    geIsOutSideClickCloseEnabled
  );
  const isEnableCloseButtonForDesktop = useDateSelector(
    geIsEnableCloseButtonForDesktop
  );
  const isDisplayCloseButton = isEnableCloseButtonForDesktop || !isOutSideClickCloseEnabled;
  const isEnabledNewTimeLabel = useTimeSelector(getNewLabelEnabled);
  const timeLabels = getTimeLabels(isEnabledNewTimeLabel);

  const { formatMessage } = useIntl();

  return (
    <Surface
      variant="foreground"
      className={clsx(styles.popupDesktopWrapper, 'margin-1-lr')}
    >
      {startDate && (
        <div aria-live="assertive" className="sr-only">
          {formatMessage(
            { id: 'aria.start_date_update' },
            { startDateLocalFormat }
          )}
          {startTime && hasTimeDropdown && (
            <span id="startTime"> {startTime} </span>
          )}
        </div>
      )}
      {endDate && (
        <div aria-live="assertive" className="sr-only">
          {formatMessage(
            { id: 'aria.end_date_update' },
            { endDateLocalFormat }
          )}
          {endTime && hasTimeDropdown && <span id="endTime"> {endTime} </span>}
        </div>
      )}
      <div
        role="dialog"
        aria-label={formatMessage({ id: 'aria.popup.chooseDate' })}
        className={clsx(styles.popupDesktop, 'flex pad-1')}
        id="date_popup"
      >
        <DateTimeField
          shownDate={minShownDate}
          placeholder={placeholder.startDate}
          onChangeInput={setStartDate}
          onChangeTime={setStartTime}
          stateKey="start"
          hasTimeDropdown={hasTimeDropdown}
          timeAriaLabel={formatMessage({
            id: timeLabels.timeAriaLabelForDeparture,
          })}
          className="flex"
        />
        {isDateRange && (
          <>
            <DateTimeField
              shownDate={startDate}
              placeholder={placeholder.endDate}
              onChangeInput={setEndDate}
              onChangeTime={setEndTime}
              stateKey="end"
              hasTimeDropdown={hasTimeDropdown}
              timeAriaLabel={formatMessage({
                id: timeLabels.timeAriaLabelForArrival,
              })}
              className={clsx(
                hasTimeDropdown ? 'margin-l' : 'margin-1-l',
                'flex'
              )}
            />
            <div className="hidden" id="date-picker-field-description">
              Enter a date or use the up and down arrow keys to change the
              current date.
            </div>
          </>
        )}
        {isDisplayCloseButton && (
          <Button
            className="margin-1-l flex flex-align-self-start"
            icon={<IconClose size="md" />}
            onClick={onClosePopup}
            aria-label="Close"
            variant="tertiary"
          />
        )}
      </div>
      <div aria-hidden="true">
        <CalendarHorizontal />
      </div>
      <CalendarFooter onClose={onClosePopup} />
    </Surface>
  );
};

PopupDesktop.propTypes = {
  placeholder: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string,
  }),
  onClosePopup: PropTypes.func.isRequired,
};

PopupDesktop.defaultProps = {
  placeholder: {},
};

export default memo(PopupDesktop);
